import { Box, Button, Flex, Grid, HStack, IconButton, Stack, Table, Text } from "@chakra-ui/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AppSelect } from "./app-select";

export interface Column<T> {
  key: keyof T | string;
  header: string;
  render?: (row: T) => ReactNode;
  hideBelow?: "sm" | "md" | "lg" | "xl";
  numeric?: boolean;
  mobilePriority?: "primary" | "secondary" | "hidden";
}

export function DataTable<T extends { id?: string | number }>({
  rows,
  columns,
  empty = "No records",
  pageSize = 10,
  pageSizeOptions = [10, 25, 50],
  pagination = true,
  rowLabel = "records",
}: {
  rows: T[];
  columns: Column<T>[];
  empty?: string;
  pageSize?: number;
  pageSizeOptions?: number[];
  pagination?: boolean;
  rowLabel?: string;
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const totalPages = Math.max(1, Math.ceil(rows.length / currentPageSize));

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const pagedRows = pagination
    ? rows.slice(
        (currentPage - 1) * currentPageSize,
        currentPage * currentPageSize,
      )
    : rows;
  const pageNumbers = useMemo(
    () => paginationWindow(currentPage, totalPages),
    [currentPage, totalPages],
  );

  if (!rows.length)
    return (
      <Flex minH="180px" align="center" justify="center" textAlign="center">
        <Stack gap="1.5">
          <Text color="fg.default" fontSize="sm" fontWeight="700">No results</Text>
          <Text color="fg.muted" fontSize="xs">{empty}</Text>
        </Stack>
      </Flex>
    );

  const cell = (row: T, column: Column<T>) => column.render ? column.render(row) : String((row as Record<string, unknown>)[column.key as string] ?? "—");
  const visibleMobileColumns = columns.filter((column) => column.mobilePriority !== "hidden");
  const mobileColumns = visibleMobileColumns.filter((column, index) => index < 5 || column.mobilePriority === "primary");

  return (
    <>
      <Stack display={{ base: "flex", md: "none" }} gap="2.5">
        {pagedRows.map((row, rowIndex) => (
          <Box key={String(row.id ?? rowIndex)} p="3.5" borderWidth="1px" borderColor="border.default" borderRadius="4px" bg="bg.subtle">
            <Stack gap="2.5">
              {mobileColumns.map((column, index) => (
                <Grid key={String(column.key)} templateColumns={index === 0 ? "1fr" : "92px minmax(0,1fr)"} gap="2" alignItems="center">
                  {index > 0 && <Text fontSize="9px" color="fg.subtle" textTransform="uppercase" letterSpacing=".04em">{column.header}</Text>}
                  <Box minW="0" textAlign={column.numeric ? "end" : "start"}>{cell(row, column)}</Box>
                </Grid>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>

      <Box display={{ base: "none", md: "block" }} overflowX="auto" mx="-2" px="2">
        <Table.Root size="sm" variant="line" minW="760px">
          <Table.Header>
            <Table.Row>
              {columns.map((column) => <Table.ColumnHeader key={String(column.key)} display={column.hideBelow ? { base: "none", [column.hideBelow]: "table-cell" } : undefined} textAlign={column.numeric ? "end" : "start"} color="fg.subtle" fontSize="10px" fontWeight="800" letterSpacing=".04em" textTransform="uppercase" py="3">{column.header}</Table.ColumnHeader>)}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {pagedRows.map((row, rowIndex) => (
              <Table.Row key={String(row.id ?? rowIndex)} _hover={{ bg: "bg.subtle" }} transition="background .12s ease">
                {columns.map((column) => <Table.Cell key={String(column.key)} display={column.hideBelow ? { base: "none", [column.hideBelow]: "table-cell" } : undefined} textAlign={column.numeric ? "end" : "start"} py="3.5" borderColor="border.subtle" fontSize="xs" color="fg.default">{cell(row, column)}</Table.Cell>)}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>

      {pagination && (
        <Flex
          mt="3"
          pt="3"
          borderTopWidth="1px"
          borderColor="border.subtle"
          align={{ base: "stretch", md: "center" }}
          justify="space-between"
          direction={{ base: "column", md: "row" }}
          gap="3"
        >
          <Text fontSize="10px" color="fg.muted" fontVariantNumeric="tabular-nums">
            Showing {(currentPage - 1) * currentPageSize + 1}–{Math.min(currentPage * currentPageSize, rows.length)} of {rows.length} {rowLabel}
          </Text>
          <Flex
            align={{ base: "stretch", sm: "center" }}
            direction={{ base: "column", sm: "row" }}
            justify="flex-end"
            gap="3"
          >
            <HStack gap="2">
              <Text whiteSpace="nowrap" fontSize="10px" color="fg.muted">Rows per page</Text>
              <AppSelect
                ariaLabel="Rows per page"
                value={String(currentPageSize)}
                width="78px"
                options={pageSizeOptions.map((size) => ({ label: String(size), value: String(size) }))}
                onValueChange={(value) => {
                  setCurrentPageSize(Number(value));
                  setCurrentPage(1);
                }}
              />
            </HStack>
            <HStack gap="1" justify={{ base: "space-between", sm: "flex-end" }}>
              <IconButton
                aria-label="Previous page"
                size="xs"
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              >
                <Box as={ChevronLeft} boxSize="3.5" />
              </IconButton>
              {pageNumbers.map((page) => (
                <Button
                  key={page}
                  size="xs"
                  minW="28px"
                  variant={page === currentPage ? "solid" : "ghost"}
                  bg={page === currentPage ? "brand.500" : undefined}
                  color={page === currentPage ? "white" : "fg.muted"}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Button>
              ))}
              <IconButton
                aria-label="Next page"
                size="xs"
                variant="outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              >
                <Box as={ChevronRight} boxSize="3.5" />
              </IconButton>
            </HStack>
          </Flex>
        </Flex>
      )}
    </>
  );
}

function paginationWindow(current: number, total: number): number[] {
  const size = Math.min(5, total);
  const start = Math.max(1, Math.min(current - 2, total - size + 1));
  return Array.from({ length: size }, (_, index) => start + index);
}
